import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useCompanyRegistrationForm,
  type CompanyRegistrationFormValues,
} from "./CompanyRegistrationForm";
import { useRegisterCompany } from "../../api/vacaturesonline/registerCompany";
import {
  useIntegrationConfiguration,
  useIntegrationConfigurationSchema,
  useUpdateIntegrationConfiguration,
} from "../../hooks/useIntegrationConfiguration";
import type { Integration, ModuleConfigurationSchema } from "../../types";
import { ConfigurationDialog } from "../ConfigurationDialog";

import { Separator } from "@/components/ui/v2/separator";
import { notificationQueryKeys } from "@/features/notifications/api/query-keys";
import type { ConfigurationContext } from "@/types/configuration";

type VacaturesOnlineConfigurationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: Integration;
  context: ConfigurationContext;
};

type CompanyMode = "existing" | "register";

export const VACATURES_ONLINE_INTEGRATIONS = new Set(["werkzoeken", "technicus"]);

const COMPANY_FIELD_KEYS = ["CompanyId", "CompanyName"];

export function VacaturesOnlineConfigurationDialog({
  open,
  onOpenChange,
  integration,
  context,
}: VacaturesOnlineConfigurationDialogProps) {
  const { t, i18n } = useTranslation();

  const queryClient = useQueryClient();

  const displayName = integration.displayName && i18n.exists(integration.displayName)
    ? t(integration.displayName)
    : integration.displayName;

  const schemaQuery = useIntegrationConfigurationSchema(
    integration.name,
    context,
    open
  );

  const configQuery = useIntegrationConfiguration(
    integration.name,
    context,
    open
  );

  const hasCompanyId = Boolean(configQuery.data?.CompanyId ?? configQuery.data?.companyId);
  const configReady = configQuery.isSuccess;

  const [mode, setMode] = useState<CompanyMode>("register");
  const [justRegistered, setJustRegistered] = useState(false);

  const registerMutation = useRegisterCompany(integration.name);

  const [isEnabled, setIsEnabled] = useState(integration.isActive);

  const showModeToggle = configReady && !hasCompanyId && !justRegistered;
  const isRegisterMode = showModeToggle && mode === "register";

  type FormValues = Record<string, string | { id: string; values: Record<string, string> }[]>;
  const formMethodsRef = useRef<{
    setError?: UseFormSetError<FormValues>;
    getValues?: () => FormValues;
      }>({});

  const updateMutation = useUpdateIntegrationConfiguration<FormValues>(context, {
    getFormMethods: () => ({
      setError: formMethodsRef.current.setError,
      getValues: formMethodsRef.current.getValues,
    }),
  });

  const handleRegister = async (values: CompanyRegistrationFormValues) => {
    if (context.level !== "client" || !context.tenantId) return;

    try {
      const result = await registerMutation.mutateAsync({
        companyName: values.companyName,
        city: values.city,
        description: values.description,
        contactName: values.contactName,
        contactEmail: values.contactEmail,
        tenantId: context.tenantId,
        clientId: context.level === "client" ? context.clientId : undefined,
        companyPhone: values.companyPhone || undefined,
        websiteUrl: values.websiteUrl || undefined,
        companyType: values.companyType || undefined,
        contactPhone: values.contactPhone || undefined,
      });

      await updateMutation.mutateAsync({
        integrationName: integration.name,
        configurations: {
          CompanyId: result.companyId,
          CompanyName: result.companyName,
        },
        isActive: true,
        currentIsActive: integration.isActive,
      });

      await queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });

      setJustRegistered(true);
      setMode("existing");
      onOpenChange(false);
    } catch {
      // Error toasts are handled by the mutation hooks' onError callbacks
    }
  };

  // Hook always called — never conditional
  const registration = useCompanyRegistrationForm({
    onSubmit: handleRegister,
    onCancel: () => onOpenChange(false),
    isSubmitting: registerMutation.isPending || updateMutation.isPending,
    integrationDisplayName: displayName,
    disabled: !isEnabled,
  });

  const filteredSchemaQuery = useMemo(() => {
    if (!isRegisterMode || !schemaQuery.data) return schemaQuery;

    const filtered: ModuleConfigurationSchema = {
      ...schemaQuery.data,
      "form-fields": schemaQuery.data["form-fields"]?.filter(
        (field) => !COMPANY_FIELD_KEYS.includes(field.key)
      ),
    };

    return { ...schemaQuery, data: filtered };
  }, [isRegisterMode, schemaQuery]);

  const handleSave = async (configurations: Record<string, string>, isActive: boolean) => {
    await updateMutation.mutateAsync({
      integrationName: integration.name,
      configurations,
      isActive,
      currentIsActive: integration.isActive,
    });
  };

  // Radio cards + stepper form
  const aboveFields = showModeToggle ? (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <RadioCard
          selected={mode === "existing"}
          onClick={() => setMode("existing")}
          title={t("integrations.registerCompany.modeExisting", "Use an existing company")}
          description={t(
            "integrations.registerCompany.modeExistingDesc",
            "Enter the Company ID and Name already registered at {{name}}",
            { name: displayName }
          )}
        />
        <RadioCard
          selected={mode === "register"}
          onClick={() => setMode("register")}
          title={t("integrations.registerCompany.modeRegister", "Register a new company")}
          description={t(
            "integrations.registerCompany.modeRegisterDesc",
            "Create a company account at {{name}} — an activation email will be sent to the contact person",
            { name: displayName }
          )}
        />
      </div>

      <Separator />

      {isRegisterMode && registration.formElement}
    </div>
  ) : undefined;

  return (
    <ConfigurationDialog
      open={open}
      onOpenChange={onOpenChange}
      title={integration.displayName}
      description={integration.description}
      isActive={integration.isActive}
      schemaQuery={isRegisterMode ? filteredSchemaQuery : schemaQuery}
      configQuery={configQuery}
      onSave={handleSave}
      isLoading={updateMutation.isPending}
      context={context}
      showEnableToggle
      onEnabledChange={setIsEnabled}
      renderAboveFields={aboveFields}
      renderFooter={isRegisterMode ? registration.footerElement : undefined}
      onFormMethodsReady={(setError, getValues) => {
        formMethodsRef.current.setError = setError;
        formMethodsRef.current.getValues = getValues as () => FormValues;
      }}
    />
  );
}

// ── Radio card ──

type RadioCardProps = {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
};

function RadioCard({ selected, onClick, title, description }: RadioCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-all ${
        selected
          ? "border-primary bg-accent shadow-sm"
          : "border-border hover:border-muted-foreground"
      }`}
    >
      <span
        className={`mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-full border-[2px] transition-all ${
          selected ? "border-primary" : "border-muted-foreground/40"
        }`}
      >
        {selected && <span className="size-[10px] rounded-full bg-primary" />}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium leading-tight">{title}</span>
        <span className="text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}
